/**
 * GBI Animations — The Vault
 * Pure CSS + IntersectionObserver. No dependencies.
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var IS_EDITOR = Boolean(window.Shopify && window.Shopify.designMode);

  /* ── Shared scroll bus (single RAF-throttled listener) ── */
  var scrollHandlers = [];
  var scrollTicking = false;
  window.addEventListener('scroll', function () {
    if (!scrollTicking) {
      requestAnimationFrame(function () {
        var scrollY = window.scrollY;
        for (var i = 0; i < scrollHandlers.length; i++) {
          scrollHandlers[i](scrollY);
        }
        scrollTicking = false;
      });
      scrollTicking = true;
    }
  }, { passive: true });

  /* ── Handler/observer references for cleanup ── */
  var _parallaxResizeHandler = null;
  var _parallaxScrollHandler = null;
  var _floatScrollHandler = null;
  var _backToTopScrollHandler = null;
  var _revealObserver = null;
  var _counterObserver = null;
  var _magneticAbort = null;
  var _magneticResizeHandler = null;

  function _removeScrollHandler(handler) {
    if (!handler) return;
    var idx = scrollHandlers.indexOf(handler);
    if (idx !== -1) scrollHandlers.splice(idx, 1);
  }

  /* ── Scroll Reveal ── */
  function initReveal() {
    if (_revealObserver) {
      _revealObserver.disconnect();
      _revealObserver = null;
    }

    if (REDUCED_MOTION || IS_EDITOR) {
      document.querySelectorAll('[data-reveal]').forEach(function (el) {
        el.classList.add('revealed');
      });
      return;
    }

    // Immediately reveal above-fold elements (no animation) to prevent FOUC
    var fold = window.innerHeight;
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < fold) {
        el.style.transition = 'none';
        el.classList.add('revealed');
        // Re-enable transitions after paint so below-fold scrolling still animates
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { el.style.transition = ''; });
        });
      }
    });

    _revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
          setTimeout(function () {
            el.classList.add('revealed');
          }, delay);
          _revealObserver.unobserve(el);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('[data-reveal]:not(.revealed)').forEach(function (el) {
      _revealObserver.observe(el);
    });
  }

  /* ── Stagger Grid (legacy data-stagger) ── */
  function initStagger() {
    if (REDUCED_MOTION || IS_EDITOR) return;

    document.querySelectorAll('[data-stagger]').forEach(function (container) {
      var items = container.querySelectorAll('[data-reveal]');
      items.forEach(function (item, i) {
        item.setAttribute('data-reveal-delay', String(i * 50));
      });
    });
  }

  /* ── Reveal Stagger (data-reveal-stagger) ── */
  function initRevealStagger() {
    document.querySelectorAll('[data-reveal-stagger]').forEach(function (container) {
      var interval = parseInt(container.getAttribute('data-reveal-stagger') || '100', 10);
      var children = container.querySelectorAll('[data-reveal]');
      children.forEach(function (child, i) {
        child.setAttribute('data-reveal-delay', String(i * interval));
      });
    });
  }

  /* ── Floating Parallax (decorative elements, desktop) ── */
  function initFloatingParallax() {
    _removeScrollHandler(_floatScrollHandler);
    _floatScrollHandler = null;

    if (REDUCED_MOTION || IS_EDITOR) return;
    if (window.innerWidth < 768) return;

    var els = document.querySelectorAll('[data-float-parallax]');
    if (!els.length) return;

    function onScroll(scrollY) {
      els.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-float-parallax') || '0.08');
        var y = scrollY * speed;
        el.style.transform = 'translateY(' + y + 'px)';
      });
    }

    _floatScrollHandler = onScroll;
    scrollHandlers.push(onScroll);
    onScroll(window.scrollY);
  }

  /* ── Parallax (hero images only, desktop) ── */
  function initParallax() {
    if (_parallaxResizeHandler) {
      window.removeEventListener('resize', _parallaxResizeHandler);
      _parallaxResizeHandler = null;
    }
    _removeScrollHandler(_parallaxScrollHandler);
    _parallaxScrollHandler = null;

    if (REDUCED_MOTION || IS_EDITOR) return;
    if (window.innerWidth < 768) return;

    var els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;

    els.forEach(function (el) { el.style.willChange = 'transform'; });

    var offsets = [];
    function cacheOffsets() {
      offsets = [];
      els.forEach(function (el) {
        offsets.push(el.offsetTop);
      });
    }
    cacheOffsets();
    var resizeTimer;
    _parallaxResizeHandler = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(cacheOffsets, 300);
    };
    window.addEventListener('resize', _parallaxResizeHandler, { passive: true });

    function onScroll(scrollY) {
      els.forEach(function (el, i) {
        var speed = parseFloat(el.getAttribute('data-parallax') || '0.15');
        var y = (scrollY - offsets[i]) * speed;
        el.style.transform = 'scale(1.1) translateY(' + y + 'px)';
      });
    }

    _parallaxScrollHandler = onScroll;
    scrollHandlers.push(onScroll);
    onScroll(window.scrollY);
  }

  /* ── Counter Animation ── */
  function initCounters() {
    if (_counterObserver) {
      _counterObserver.disconnect();
      _counterObserver = null;
    }

    if (REDUCED_MOTION || IS_EDITOR) {
      document.querySelectorAll('[data-counter]').forEach(function (el) {
        var prefix = el.getAttribute('data-counter-prefix') || '';
        var suffix = el.getAttribute('data-counter-suffix') || '';
        el.textContent = prefix + el.getAttribute('data-counter') + suffix;
      });
      return;
    }

    _counterObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var raw = el.getAttribute('data-counter') || '';
          var target = parseFloat(raw);
          var suffix = el.getAttribute('data-counter-suffix') || '';
          var prefix = el.getAttribute('data-counter-prefix') || '';
          // Non-numeric value (e.g., "Free") — render as-is, skip animation.
          if (!isFinite(target)) {
            el.textContent = prefix + raw + suffix;
            _counterObserver.unobserve(el);
            return;
          }
          // Preserve decimal precision from the source value so 4.9 doesn't
          // round up to 5 mid-animation. Math.round was clobbering the
          // saved "Seller Rating" decimal.
          var decimalMatch = raw.match(/\.([0-9]+)/);
          var decimals = decimalMatch ? decimalMatch[1].length : 0;
          var duration = 1200;
          var startTime = null;
          var frameCount = 0;

          function format(n) {
            return decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
          }

          function animate(time) {
            if (!startTime) startTime = time;
            var progress = Math.min((time - startTime) / duration, 1);
            if (++frameCount % 2 === 0 || progress >= 1) {
              var eased = 1 - Math.pow(1 - progress, 3);
              el.textContent = prefix + format(target * eased) + suffix;
            }
            if (progress < 1) requestAnimationFrame(animate);
          }
          requestAnimationFrame(animate);
          _counterObserver.unobserve(el);
        });
      },
      { threshold: 0.3 }
    );

    document.querySelectorAll('[data-counter]').forEach(function (el) {
      el.textContent = '0';
      _counterObserver.observe(el);
    });
  }

  /* ── Magnetic Hover (hero CTA only) ── */
  function initMagnetic() {
    if (_magneticAbort) {
      _magneticAbort.abort();
      _magneticAbort = null;
    }
    if (_magneticResizeHandler) {
      window.removeEventListener('resize', _magneticResizeHandler);
      _magneticResizeHandler = null;
    }

    if (REDUCED_MOTION) return;
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

    _magneticAbort = new AbortController();
    var signal = _magneticAbort.signal;

    var buttons = document.querySelectorAll('.btn-magnetic');
    if (!buttons.length) return;

    /* Invalidate cached rects on resize so magnetic transform uses fresh coordinates */
    var resizeTimer;
    _magneticResizeHandler = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        buttons.forEach(function (btn) { btn._rect = null; });
      }, 150);
    };
    window.addEventListener('resize', _magneticResizeHandler, { passive: true });

    buttons.forEach(function (btn) {
      btn._moveTicking = false;
      btn._rect = null;
      btn.addEventListener('mouseenter', function () {
        btn._rect = btn.getBoundingClientRect();
        btn.style.willChange = 'transform';
      }, { signal: signal });
      btn.addEventListener('mousemove', function (e) {
        if (btn._moveTicking) return;
        btn._moveTicking = true;
        requestAnimationFrame(function () {
          var r = btn._rect;
          if (r) {
            var x = e.clientX - r.left - r.width / 2;
            var y = e.clientY - r.top - r.height / 2;
            btn.style.transform = 'translate(' + (x * 0.15) + 'px, ' + (y * 0.15) + 'px)';
          }
          btn._moveTicking = false;
        });
      }, { signal: signal });
      btn.addEventListener('mouseleave', function () {
        btn._rect = null;
        btn.style.transform = '';
        btn.style.transition = 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1)';
        setTimeout(function () {
          btn.style.transition = '';
          btn.style.willChange = '';
        }, 400);
      }, { signal: signal });
    });
  }

  /* ── Back to Top ── */
  function initBackToTop() {
    _removeScrollHandler(_backToTopScrollHandler);
    _backToTopScrollHandler = null;

    var btn = document.getElementById('back-to-top');
    if (!btn) return;

    _backToTopScrollHandler = function (scrollY) {
      if (scrollY > 600) {
        btn.classList.add('visible');
      } else {
        btn.classList.remove('visible');
      }
    };
    scrollHandlers.push(_backToTopScrollHandler);

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    });
  }

  /* ── Idle callback helper ── */
  var idle = ('requestIdleCallback' in window)
    ? function (fn) { requestIdleCallback(fn); }
    : function (fn) { setTimeout(fn, 200); };

  /* ── Init ── */
  function init() {
    /* Critical: reveal system runs first to prevent FOUC */
    initStagger();
    initRevealStagger();
    initReveal();

    /* Non-critical: defer desktop-only effects and counters to idle time */
    idle(function () {
      initParallax();
      initFloatingParallax();
      initCounters();
      initMagnetic();
      initBackToTop();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Cleanup: disconnect all observers, clear all handlers ── */
  function cleanup() {
    if (_revealObserver) {
      _revealObserver.disconnect();
      _revealObserver = null;
    }
    if (_counterObserver) {
      _counterObserver.disconnect();
      _counterObserver = null;
    }
    /* Clear ALL scroll handlers at once — prevents unbounded growth on re-init */
    scrollHandlers.length = 0;
    _parallaxScrollHandler = null;
    _floatScrollHandler = null;
    _backToTopScrollHandler = null;
    if (_parallaxResizeHandler) {
      window.removeEventListener('resize', _parallaxResizeHandler);
      _parallaxResizeHandler = null;
    }
    if (_magneticAbort) {
      _magneticAbort.abort();
      _magneticAbort = null;
    }
    if (_magneticResizeHandler) {
      window.removeEventListener('resize', _magneticResizeHandler);
      _magneticResizeHandler = null;
    }
  }

  window.GBI = window.GBI || {};
  window.GBI.reinitAnimations = function () {
    initStagger();
    initRevealStagger();
    initReveal();
  };
  window.GBI.cleanupAnimations = cleanup;

  /* Re-init on Shopify section events */
  document.addEventListener('shopify:section:load', function () {
    cleanup();
    initStagger();
    initRevealStagger();
    initReveal();
    initParallax();
    initFloatingParallax();
    initCounters();
    initMagnetic();
    initBackToTop();
  });
})();
